import { colorsTuple, createTheme, MantineTheme, virtualColor } from '@mantine/core';

// Custom CSS variables resolver to properly override Mantine's background variables
export const cssVariablesResolver = (theme: MantineTheme) => ({
  variables: {
    // Variables that don't change between color schemes
    '--mantine-scale': theme.scale.toString(),
  },
  light: {
    // Light mode background overrides - improved contrast
    '--mantine-color-body': '#FAF4EA',                  // Warmer, more distinct body background
    '--mantine-color-default': '#FFFBF7',              // Slightly off-white for surfaces
    '--mantine-color-default-hover': '#F5EDE1',        // More noticeable hover state
    '--mantine-color-default-border': '#E5D5C8',       // Darker, more visible borders
  },
  dark: {
    // Dark mode background overrides - warm tones to match gradient
    '--mantine-color-body': '#0a0300',              // Matches gradient end color
    '--mantine-color-default': '#180f00',           // Dark warm surface
    '--mantine-color-default-hover': '#201200',     // Slightly lighter warm hover
    '--mantine-color-default-border': '#302000',    // Warm subtle borders

    // Override specific dark color variables that components use - warm tones
    '--mantine-color-dark-6': '#180f00',            // Card backgrounds (warm)
    '--mantine-color-dark-7': '#0a0300',            // Main backgrounds (matches gradient)
    '--mantine-color-dark-5': '#201200',            // Hover states (warm)
    '--mantine-color-dark-4': '#302000',            // Borders (warm)
    '--mantine-color-dark-8': '#050200',            // Deep backgrounds (very dark warm)
  }
});

// Theme 1: Enlightened Teal
// const enlightenedTealTheme = createTheme({
//   colors: {
//     // Generic color names that work across all themes
//     'brand-primary': colorsTuple('#008C75'),      // Enlightened Teal
//     'brand-secondary': colorsTuple('#66CCBD'),    // Serene Aqua
//     'brand-accent': colorsTuple('#9F7AEA'),       // Inspire Violet
//     'brand-accent-alt': colorsTuple('#FFD700'),   // Glimmer Gold
//     'neutral-light': colorsTuple('#F7F7F7'),      // Pure Canvas
//     'neutral-dark': colorsTuple('#283C4A'),       // Deep Odyssey
//     'alert-color': colorsTuple('#E57373'),        // Vivid Ember

//     // Legacy color names for backward compatibility
//     'deep-teal': colorsTuple('#008C75'),
//     'muted-aqua': colorsTuple('#66CCBD'),
//     'lavender-blue': colorsTuple('#9F7AEA'),
//     'off-white': colorsTuple('#F7F7F7'),
//     'dark-blue': colorsTuple('#283C4A'),
//     'sunset-orange': colorsTuple('#E57373'),

//     primary: virtualColor({
//       name: 'primary',
//       light: 'brand-primary',
//       dark: 'brand-secondary',
//     }),
//   },
//   primaryColor: 'primary',
//   other: {
//     anchor: 'primary',
//   },
// });

// Theme 2: Coral & Aqua
// const coralAquaTheme = createTheme({
//   colors: {
//     // Generic color names that work across all themes
//     'brand-primary': colorsTuple('#FF6F61'),      // Coral
//     'brand-secondary': colorsTuple('#D2B48C'),    // Sandy Beige
//     'brand-accent': colorsTuple('#00BFFF'),       // Aqua
//     'brand-accent-alt': colorsTuple('#00BFFF'),   // Same as accent
//     'neutral-light': colorsTuple('#FFFDD0'),      // Cream
//     'neutral-dark': colorsTuple('#8B8B7A'),       // Warm Gray
//     'alert-color': colorsTuple('#FF7F50'),        // Sunset Orange

//     // Legacy color names for backward compatibility
//     'deep-teal': colorsTuple('#FF6F61'),
//     'muted-aqua': colorsTuple('#D2B48C'),
//     'lavender-blue': colorsTuple('#00BFFF'),
//     'off-white': colorsTuple('#FFFDD0'),
//     'dark-blue': colorsTuple('#8B8B7A'),
//     'sunset-orange': colorsTuple('#FF7F50'),

//     primary: virtualColor({
//       name: 'primary',
//       light: 'brand-primary',
//       dark: 'brand-secondary',
//     }),
//   },
//   primaryColor: 'primary',
//   other: {
//     anchor: 'primary',
//   },
// });

// Theme 3: Terracotta Sunset
const terracottaSunsetTheme = createTheme({
  colors: {
    // Generic color names that work across all themes
    'brand-primary': colorsTuple('#E87040'),      // Terracotta Sunset
    'brand-secondary': colorsTuple('#F7A38B'),    // Muted Coral
    'brand-accent': colorsTuple('#20A0B0'),       // Ocean Teal
    'brand-accent-alt': colorsTuple('#FFC94A'),   // Golden Sand
    'neutral-light': colorsTuple('#FDF7EC'),      // Coastal Linen
    'neutral-dark': colorsTuple('#324B5D'),       // Deep Marine
    'alert-color': colorsTuple('#BF360C'),        // Rustic Red

    // Legacy color names for backward compatibility
    'deep-teal': colorsTuple('#E87040'),
    'muted-aqua': colorsTuple('#F7A38B'),
    'lavender-blue': colorsTuple('#20A0B0'),
    'off-white': colorsTuple('#FDF7EC'),
    'dark-blue': colorsTuple('#324B5D'),
    'sunset-orange': colorsTuple('#BF360C'),

    primary: virtualColor({
      name: 'primary',
      light: 'brand-primary',
      dark: 'brand-secondary',
    }),
  },
  primaryColor: 'primary',
  other: {
    anchor: 'primary',
  },
});

// Theme 4: Deep Cosmos (Dark Theme)
// const deepCosmosTheme = createTheme({
//   colors: {
//     // Generic color names that work across all themes
//     'brand-primary': colorsTuple('#6C4B8C'),      // Nebula Indigo
//     'brand-secondary': colorsTuple('#A88ACB'),    // Starlight Violet
//     'brand-accent': colorsTuple('#4DE2C9'),       // Hologram Blue
//     'brand-accent-alt': colorsTuple('#FF7ACC'),   // Andromeda Pink
//     'neutral-light': colorsTuple('#F9F1FB'),      // Cosmic Dust
//     'neutral-dark': colorsTuple('#B7B0C7'),       // Deep Void Text
//     'alert-color': colorsTuple('#FF4A4A'),        // Supernova Red

//     // Legacy color names for backward compatibility
//     'deep-teal': colorsTuple('#6C4B8C'),
//     'muted-aqua': colorsTuple('#A88ACB'),
//     'lavender-blue': colorsTuple('#4DE2C9'),
//     'off-white': colorsTuple('#F9F1FB'),
//     'dark-blue': colorsTuple('#B7B0C7'),
//     'sunset-orange': colorsTuple('#FF4A4A'),

//     primary: virtualColor({
//       name: 'primary',
//       light: 'brand-primary',
//       dark: 'brand-secondary',
//     }),
//   },
//   primaryColor: 'primary',
//   other: {
//     anchor: 'primary',
//   },
// });

// Original GPT Muse theme
// const originalTheme = createTheme({
//   colors: {
//     // Generic color names that work across all themes
//     'brand-primary': colorsTuple('#00796B'),      // Deep Teal
//     'brand-secondary': colorsTuple('#4DB6AC'),    // Muted Aqua
//     'brand-accent': colorsTuple('#7B68EE'),       // Lavender Blue
//     'brand-accent-alt': colorsTuple('#7B68EE'),   // Same as accent
//     'neutral-light': colorsTuple('#FAFAFA'),      // Off-White
//     'neutral-dark': colorsTuple('#2C3E50'),       // Dark Blue
//     'alert-color': colorsTuple('#FF7043'),        // Sunset Orange

//     // Original color names for backward compatibility
//     'deep-teal': colorsTuple('#00796B'),
//     'muted-aqua': colorsTuple('#4DB6AC'),
//     'lavender-blue': colorsTuple('#7B68EE'),
//     'off-white': colorsTuple('#FAFAFA'),
//     'dark-blue': colorsTuple('#2C3E50'),
//     'sunset-orange': colorsTuple('#FF7043'),

//     primary: virtualColor({
//       name: 'primary',
//       light: 'brand-primary',
//       dark: 'brand-secondary',
//     }),
//   },
//   primaryColor: 'primary',
//   other: {
//     anchor: 'primary',
//   },
// });

// Export the active theme - uncomment the one you want to use
// export const theme = enlightenedTealTheme;
// export const theme = coralAquaTheme;
export const theme = terracottaSunsetTheme;
// export const theme = deepCosmosTheme;
// export const theme = originalTheme;
